import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, FileQuestionMark, Settings } from 'lucide-react';
import TileButton from '../shared/TileButton';
import AppRouter from '../../AppRouter';
import { RoleDashboardRouter } from '../dashboards/RoleDashboardRouter';
import { AdminPanel } from '../admin';
import Header from '../shared/Header';
import { useAuthValidation } from '../../hooks/useAuthValidation';
import { apiClient } from '../../utils/apiClient';

interface DashboardProps {
  onLogout: () => void;
}

interface UserData {
  id: string;
  name: string;
  employeeNumber: string;
  role: string;
}

interface DashboardData {
  user: UserData;
  hasReproveAccess: boolean;
  welcomeMessage: string;
}

interface DashboardStats {
  totalUsers: number;
  totalSalesPoints: number;
  totalRepresentatives: number;
  totalSellers: number;
}

type ViewType = 'dashboard' | 'reprove' | 'admin';
type AppSection = 'reprove' | 'reports';

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const { isValidating, isValid, logout } = useAuthValidation();
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [currentAppSection, setCurrentAppSection] =
    useState<AppSection>('reprove');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(
    null
  );

  const loadDashboard = useCallback(async (): Promise<void> => {
    if (!isValid) return;

    setLoading(true);
    setError('');

    try {
      const data = await apiClient.get<DashboardData>('/dashboard');
      setDashboardData(data);

      if (
        data.user.role === 'SYSTEM_ADMIN' ||
        data.user.role === 'ADMIN' ||
        data.user.role === 'GENERAL_MANAGER' ||
        data.user.role === 'ZONE_MANAGER' ||
        data.user.role === 'MKT'
      ) {
        try {
          const stats = await apiClient.get<DashboardStats>('/dashboard/stats');
          setDashboardStats(stats);
        } catch (err) {
          console.error('Error al cargar estadísticas:', err);
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Error al cargar el dashboard'
      );
    } finally {
      setLoading(false);
    }
  }, [isValid]);

  useEffect(() => {
    if (isValid && !isValidating) {
      void loadDashboard();
    }
  }, [isValid, isValidating, loadDashboard]);

  const handleLogout = (): void => {
    logout();
    onLogout();
  };

  const handleBackToDashboard = (): void => {
    setCurrentView('dashboard');
    setCurrentAppSection('reprove');
    setIsMobileMenuOpen(false);
  };

  const handleNavigateToReprove = (): void => {
    setCurrentView('reprove');
    setCurrentAppSection('reprove');
    setIsMobileMenuOpen(false);

    if (currentView === 'reprove') {
      setTimeout(() => {
        const event = new CustomEvent('navigateToReprove');
        window.dispatchEvent(event);
      }, 100);
    }
  };

  const handleNavigateToAdmin = (): void => {
    setCurrentView('admin');
    setIsMobileMenuOpen(false);
  };

  // Handle navigation del header
  const handleHeaderNavigation = (
    section: 'reprove' | 'reports' | 'admin'
  ): void => {
    setIsMobileMenuOpen(false);

    if (section === 'admin') {
      setCurrentView('admin');
    } else if (section === 'reports') {
      setCurrentView('reprove');
      setCurrentAppSection('reports');
      // Disparar evento para AppRouter
      setTimeout(() => {
        const event = new CustomEvent('navigateToReports');
        window.dispatchEvent(event);
      }, 0);
    } else if (section === 'reprove') {
      setCurrentView('reprove');
      setCurrentAppSection('reprove');
      // 🔥 NUEVO: Disparar evento para AppRouter
      setTimeout(() => {
        const event = new CustomEvent('navigateToReprove');
        window.dispatchEvent(event);
      }, 0);
    }
  };

  const handleAppNavigation = (section: AppSection): void => {
    setCurrentAppSection(section);
  };

  // Loading state mientras valida token
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-medium mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">
            Validando sesión...
          </p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, el App.tsx maneja esto
  if (!isValid) {
    onLogout();
    return null;
  }

  // Loading state mientras carga datos del dashboard
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-medium mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">
            Cargando dashboard...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 sm:px-6 rounded-lg mb-4">
            <p className="font-medium text-sm sm:text-base">
              Error al cargar el dashboard
            </p>
            <p className="text-xs sm:text-sm mt-1">{error}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center">
            <button
              onClick={() => void loadDashboard()}
              className="w-full sm:w-auto bg-primary-dark hover:bg-primary-medium text-white px-6 py-3 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              Reintentar
            </button>
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si no hay datos del dashboard
  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            No se pudieron cargar los datos del dashboard
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center">
            <button
              onClick={() => void loadDashboard()}
              className="w-full sm:w-auto bg-primary-dark hover:bg-primary-medium text-white px-6 py-3 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              Reintentar
            </button>
            <button
              onClick={handleLogout}
              className="w-full sm:w-auto bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 sm:py-2 rounded-lg transition-colors text-sm sm:text-base"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getCurrentSection = (): 'reprove' | 'reports' | 'admin' => {
    if (currentView === 'admin') return 'admin';
    if (currentView === 'reprove') return currentAppSection;
    return 'reprove';
  };

  const renderContent = () => {
    switch (currentView) {
      case 'reprove':
        return (
          <AppRouter
            onBack={handleBackToDashboard}
            onNavigate={handleAppNavigation}
          />
        );

      case 'admin':
        return <AdminPanel onBack={handleBackToDashboard} />;

      case 'dashboard':
      default:
        return (
          <DashboardView
            dashboardData={dashboardData}
            dashboardStats={dashboardStats}
            onLogout={handleLogout}
            onNavigateToReprove={handleNavigateToReprove}
            onNavigateToAdmin={handleNavigateToAdmin}
            onToggleMobileMenu={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onBack={handleBackToDashboard}
        currentSection={getCurrentSection()}
        onNavigate={handleHeaderNavigation}
        onLogout={handleLogout}
        onNavigateToSurvey={handleNavigateToReprove}
        // hasReproveAccess={dashboardData.hasReproveAccess}
        hasReproveAccess={true}
      />
      {renderContent()}
    </div>
  );
};

// Componente de vista del dashboard
interface DashboardViewProps {
  dashboardData: DashboardData;
  dashboardStats: DashboardStats | null;
  onLogout: () => void;
  onNavigateToReprove: () => void;
  onNavigateToAdmin: () => void;
  onToggleMobileMenu: () => void;
}

const DashboardView: React.FC<DashboardViewProps> = ({
  dashboardData,
  dashboardStats,
  onLogout,
  onNavigateToReprove,
  onNavigateToAdmin,
}) => {
  const [activeTab, setActiveTab] = useState<'home' | 'stats'>('home');

  const canAccessAdmin =
    dashboardData.user.role === 'SYSTEM_ADMIN' ||
    dashboardData.user.role === 'ADMIN';

  const canSeeReports =
    dashboardData.user.role === 'SYSTEM_ADMIN' ||
    dashboardData.user.role === 'ADMIN' ||
    dashboardData.user.role === 'GENERAL_MANAGER' ||
    dashboardData.user.role === 'ZONE_MANAGER' ||
    dashboardData.user.role === 'MKT';

  const getRoleDisplayName = (role: string): string => {
    const roleNames = {
      SYSTEM_ADMIN: 'Administrador del Sistema',
      ADMIN: 'Administrador',
      GENERAL_MANAGER: 'Gerente General',
      ZONE_MANAGER: 'Gerente de Zona',
      REPRESENTATIVE: 'Representante',
      SELLER: 'Vendedora',
      MKT: 'Marketing',
    };
    return roleNames[role as keyof typeof roleNames] || role;
  };
  const SidebarContent = () => (
    <div className="flex flex-col justify-between h-full py-6 sm:py-8">
      <div className="space-y-3 sm:space-y-4 px-4">
        {/* Módulo principal - RE-PRO-VE */}
        {dashboardData.hasReproveAccess && (
          <TileButton
            label="Evaluación PDV"
            Icon={FileQuestionMark}
            hover="shadow"
            color="white"
            onClick={() => {
              onNavigateToReprove();
            }}
          />
        )}

        {/* Módulo de administración */}
        {canAccessAdmin && (
          <TileButton
            label="Administración"
            Icon={Settings}
            hover="shadow"
            color="primary"
            onClick={() => {
              onNavigateToAdmin();
            }}
          />
        )}
      </div>

      {/* Botón de cerrar sesión */}
      <div className="px-4">
        <TileButton
          label="Cerrar Sesión"
          Icon={LogOut}
          hover="shadow"
          color="primary"
          onClick={() => {
            onLogout();
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      {/* Contenido del dashboard */}
      <div className="flex">
        <aside className="hidden lg:flex lg:flex-col lg:justify-between lg:w-64 lg:h-auto lg:bg-white lg:shadow-sm lg:min-h-[calc(100vh-4rem)]">
          <SidebarContent />
        </aside>

        {/* Main Content */}
        <div className="flex-1 md:p-6">
          {/* Tabs de navegación */}
          {canSeeReports && (
            <div className="flex gap-2 sm:gap-4 border-b border-gray-200 mb-4 sm:mb-6 overflow-x-auto">
              <button
                onClick={() => {
                  setActiveTab('home');
                }}
                className={`pb-2 px-3 sm:px-4 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                  activeTab === 'home'
                    ? 'border-b-2 border-primary-dark text-primary-dark'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Inicio
              </button>
              <button
                onClick={() => {
                  setActiveTab('stats');
                }}
                className={`pb-2 px-3 sm:px-4 font-medium transition-colors whitespace-nowrap text-sm sm:text-base ${
                  activeTab === 'stats'
                    ? 'border-b-2 border-primary-dark text-primary-dark'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Estadísticas
              </button>
            </div>
          )}

          {/* Contenido según tab activa */}
          {activeTab === 'home' ? (
            // TAB HOME: Contenido original de bienvenida
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh-10rem)] sm:min-h-[calc(100vh-12rem)]">
              <div className="text-center max-w-2xl px-4">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-700 mb-6 sm:mb-8">
                  {dashboardData.welcomeMessage}
                </h1>

                <div className="mt-6 sm:mt-8 text-xs sm:text-sm text-gray-500">
                  <p>Usuario: {dashboardData.user.employeeNumber}</p>
                  <p>Rol: {getRoleDisplayName(dashboardData.user.role)}</p>

                  <div className="mt-3 sm:mt-4 space-y-1">
                    {canSeeReports && (
                      <p className="text-blue-600">
                        ✓ Tienes acceso a reportes
                      </p>
                    )}
                    {canAccessAdmin && (
                      <p className="text-red-600">
                        ✓ Tienes acceso de administrador
                      </p>
                    )}
                  </div>
                </div>

                {canAccessAdmin && (
                  <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h3 className="font-medium text-red-800 mb-2 text-sm sm:text-base">
                      Panel de Administrador
                    </h3>
                    <p className="text-xs sm:text-sm text-red-700">
                      Como administrador del sistema, tienes acceso completo
                      para gestionar usuarios, vendedoras, puntos de venta,
                      configuraciones y toda la información del sistema.
                    </p>
                  </div>
                )}

                {(canSeeReports || canAccessAdmin) && dashboardStats && (
                  <div className="mt-6 sm:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-center">
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600">
                        {dashboardStats.totalUsers}
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-600 mt-1">
                        Usuarios
                      </div>
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                      <div className="text-xl sm:text-2xl font-bold text-green-600">
                        {dashboardStats.totalSalesPoints}
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-600 mt-1">
                        Puntos de Venta
                      </div>
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                      <div className="text-xl sm:text-2xl font-bold text-purple-600">
                        {dashboardStats.totalRepresentatives}
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-600 mt-1">
                        Representantes
                      </div>
                    </div>
                    <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600">
                        {dashboardStats.totalSellers}
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-600 mt-1">
                        Vendedoras
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <RoleDashboardRouter />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
